import type { Project, ProjectCommandCenter, ProjectThreadResource } from '../../hooks/useProjects.js';
import { workThreadHref } from '../projects/project-utils.js';
import type { EmployeeView } from './shared';
import type { DecisionRow } from './detail/panels/DecisionsPanel.js';
import type { OutputKind, OutputRow } from './detail/panels/OutputsPanel.js';

export interface WorkforceProjectLink {
  resource: ProjectThreadResource;
  threadId: number;
  changeId: number | null;
  role: string | null;
}

export interface ReassignDraft {
  team: string;
  manager: string;
  workstream: string;
  reason: string;
}

export function buildProjectWorkforceLinks(commandCenter: ProjectCommandCenter | undefined) {
  const links = new Map<string, WorkforceProjectLink[]>();
  for (const resource of commandCenter?.resources ?? []) {
    if (resource.resourceType !== 'workforce_member') continue;
    const id = String(resource.resourceId);
    const current = links.get(id) ?? [];
    current.push({
      resource,
      threadId: resource.projectThreadId,
      changeId: resource.projectChangeId,
      role: resource.role,
    });
    links.set(id, current);
  }
  return links;
}

export function isLinkedToProject(employee: EmployeeView, links: Map<string, WorkforceProjectLink[]>) {
  return links.has(employee.id);
}

export function toProjectScopedEmployee(
  employee: EmployeeView,
  projectName: string,
  linkedToProject: boolean,
): EmployeeView {
  if (linkedToProject) {
    return { ...employee, project: projectName };
  }
  const status: EmployeeView['status'] = employee.status === 'available' ? 'available' : 'paused';
  return {
    ...employee,
    status,
    statusLabel: employee.status === 'available' ? (employee.statusLabel ?? 'Available') : 'Allocated elsewhere',
    project: null,
    team: null,
    workstream: null,
    currentChange: null,
    currentStep:
      employee.status === 'available' ? 'available for project assignment proposal' : 'not linked to this project',
    blockerShort: null,
    blockerLong: null,
    attn: false,
    tasksAssigned: 0,
    tasksAwaitingReview: 0,
    decisionsPending: 0,
  };
}

export function resourcesForEmployee(commandCenter: ProjectCommandCenter | undefined, employeeId: string) {
  const resources = commandCenter?.resources ?? [];
  const employeeLinks = resources.filter(
    (resource) => resource.resourceType === 'workforce_member' && String(resource.resourceId) === employeeId,
  );
  const threadIds = new Set(employeeLinks.map((resource) => resource.projectThreadId));
  const changeIds = new Set(
    employeeLinks
      .map((resource) => resource.projectChangeId)
      .filter((changeId): changeId is number => typeof changeId === 'number'),
  );

  return resources.filter((resource) => {
    if (resource.resourceType === 'workforce_member') return false;
    if (threadIds.has(resource.projectThreadId)) return true;
    return resource.projectChangeId != null && changeIds.has(resource.projectChangeId);
  });
}

export function outputsFromResources(resources: ProjectThreadResource[]): OutputRow[] {
  return resources.flatMap((resource) => {
    const kind = outputKind(resource.resourceType);
    if (!kind) return [];
    return [
      {
        id: `${resource.resourceType}-${resource.resourceId}-${resource.id}`,
        kind,
        name: resourceLabel(resource),
        sub: resourceSub(resource),
        when: shortRelative(resource.createdAt),
        href: workThreadHref(resource.projectId, resource.projectThreadId, resource.projectChangeId ?? undefined),
      },
    ];
  });
}

export function decisionsFromResources(resources: ProjectThreadResource[]): DecisionRow[] {
  return resources
    .filter((resource) => resource.resourceType === 'decision')
    .map((resource) => ({
      id: String(resource.id),
      question: resourceLabel(resource),
      context: resourceSub(resource),
      kind: 'approve',
    }));
}

export function planWorkforcePrompt(project: Project) {
  return [
    `Plan the workforce for "${project.name}".`,
    'Use the current project threads and changes as context.',
    'Recommend reusable employees to assign, any new hire drafts that are needed, reporting lines, permission boundaries, and first assignments.',
    'Present the plan as reviewable decision cards before anything is applied.',
  ].join('\n\n');
}

function outputKind(resourceType: ProjectThreadResource['resourceType']): OutputKind | null {
  if (resourceType === 'artifact') return 'artifact';
  if (resourceType === 'task') return 'task';
  if (resourceType === 'decision') return 'decision';
  return null;
}

function metadataString(resource: ProjectThreadResource, key: string) {
  const value = resource.metadata?.[key];
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function resourceLabel(resource: ProjectThreadResource) {
  return (
    resource.role ??
    metadataString(resource, 'title') ??
    metadataString(resource, 'name') ??
    `${resource.resourceType.replace(/_/g, ' ')} ${resource.resourceId}`
  );
}

function resourceSub(resource: ProjectThreadResource) {
  const parts = [
    resource.resourceType.replace(/_/g, ' '),
    resource.projectChangeId ? `Change #${resource.projectChangeId}` : `Thread #${resource.projectThreadId}`,
  ];
  const state = metadataString(resource, 'status') ?? metadataString(resource, 'state');
  if (state) parts.push(state);
  return parts.join(' · ');
}

function shortRelative(iso: string) {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const minutes = Math.max(0, Math.floor((Date.now() - then) / 60_000));
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? '1d' : `${days}d`;
}

export function employeeSupervisionPrompt(project: Project, employee: EmployeeView) {
  return [
    `Open a project-scoped supervision thread for ${employee.name} on "${project.name}".`,
    `Employee role: ${employee.role}. Current status: ${employee.statusLabel ?? employee.status}.`,
    employee.currentChange ? `Current work: ${employee.currentChange}.` : 'No active project work is linked yet.',
    'Recommend the next decision card, assignment, blocker resolution, or coaching action for this project context.',
  ].join('\n\n');
}

export function reassignPrompt(project: Project, employee: EmployeeView, draft: ReassignDraft) {
  return [
    `Draft a reassignment decision card for ${employee.name} on "${project.name}".`,
    `Current: team=${employee.team ?? 'unassigned'}, manager=${employee.manager ?? 'unassigned'}, workstream=${employee.workstream ?? 'unassigned'}.`,
    `Requested: team=${draft.team || 'unchanged'}, manager=${draft.manager || 'unchanged'}, workstream=${draft.workstream || 'unchanged'}.`,
    draft.reason ? `Reason: ${draft.reason}.` : 'Ask for the reassignment reason if it is required for audit.',
    'Preview impacted tasks, active workflows, pending decisions, and permission scope before asking for approval.',
  ].join('\n\n');
}
